import { Flags } from '@constants/flags';
import { AssetClassLevel, FinancialProductStrategy } from '@generated/graphql';
import {
  type FinancialProduct,
  MODEL_PORTFOLIO_STRATEGIES,
  type Theme,
} from '@interfaces/financialProduct';
import { MenuItem } from '@mui/material';
import { useFlag } from '@onevesthq/feature-flags/web';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TranslatableTextField } from '@ovComponents/2-component/translatableTextField/translatableTextField';
import { AssetClassBranchesSelect } from '@ovComponents/3-pattern/assetClassBranchesSelect/assetClassBranchesSelect';
import { NumberField } from '@ovComponents/3-pattern/numberField/numberField';
import type { Security } from '@ovComponents/3-pattern/securitiesSelectionTable/types';
import { SecuritySelect } from '@ovComponents/3-pattern/securitySelect/securitySelect';
import { SwitchField } from '@ovComponents/3-pattern/switchField/switchField';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { hasModelPortfolioChanges } from '@ovComponents/4-module/createModelPortfolioworkflow/hasModelPortfolioChanges';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { isDriftToleranceInvalid } from '@pages/setting/Trade/useOrgDriftSettings';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { enumValues } from '@utils/enumValues';
import { Alert, Box, Button, Grid, Typography, useToast } from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import type { ModelPortfolioData } from '../createModelPortfolio/createModelPortfolio.types';
import { isBranchAllocationInvalid } from '../utils/useSaveModelPortfolio';
import { DriftSettingsSection } from './components/driftSettingsSection';

export type AddModelPortfolioDetailsVisualProps = {
  options: {
    [key: string]: any;
  };
  modelPortfolioData: ModelPortfolioData;
  setModelPortfolioData: (modelPortfolioData: ModelPortfolioData) => void;
  originalValues: ModelPortfolioData;
  loading: boolean;
  continueFunc: (modelPortfolioData: ModelPortfolioData) => void;
  onSaveForLater: (modelPortfolioData: ModelPortfolioData) => void;
  themes?: Theme[];
  workflowCompletion?: {
    [key: string]: any;
  };
};

export const AddModelPortfolioDetailsVisual = ({
  options,
  modelPortfolioData,
  setModelPortfolioData,
  originalValues,
  loading,
  continueFunc,
  onSaveForLater,
  themes = [],
  workflowCompletion,
}: AddModelPortfolioDetailsVisualProps) => {
  const { translateBackendString, language } = useLocalization();
  const { t } = useTranslation(['modelPortfolios', 'shared', 'client']);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { enqueueToast } = useToast();
  const { enabled: isAssetClassPortfoliosEnabled } = useFlag(
    Flags.asset_class_portfolios,
    { default: false },
  );
  // Safe before the section ever mounts: an unset tolerance is valid, so this
  // stays a no-op until the user edits the field.
  const driftToleranceInvalid = isDriftToleranceInvalid(
    modelPortfolioData.driftTolerancePercent,
  );
  const [validationErrors, setValidationErrors] = useState<Set<string>>(
    new Set(),
  );
  const [cashEquivalentSecurity, setCashEquivalentSecurity] =
    useState<Security | null>(
      modelPortfolioData.securities?.find(
        (security: Security) => security.isCashEquivalent,
      ) as Security | null,
    );

  const validateField = (fieldName: string, value: any): boolean => {
    if (
      !options[fieldName]?.enabled ||
      options[fieldName]?.required === FieldRequiredType.NOT_REQUIRED
    ) {
      return true;
    }

    switch (fieldName) {
      case 'translatedName':
      case 'translatedUrl':
      case 'translatedPortfolioDescription':
      case 'riskProfileName':
        return value?.[language]?.trim()?.length > 0;

      case 'lowThresholdScore':
      case 'suggestedMinScore':
      case 'riskReturnLevel':
      case 'forecastedRateOfReturn':
      case 'forecastedStandardDeviation':
        return (
          value !== undefined &&
          value !== null &&
          value !== '' &&
          parseFloat(value) >= 0
        );

      case 'suggestedMaxScore': {
        const minScore = modelPortfolioData.suggestedMinScore;
        const maxScore = parseFloat(value);
        return (
          value !== undefined &&
          value !== null &&
          value !== '' &&
          (minScore === undefined ||
            minScore === null ||
            maxScore > minScore) &&
          maxScore >= 0
        );
      }

      default:
        return value !== undefined && value !== null && value !== '';
    }
  };

  const handleFieldBlur = (fieldName: string) => {
    const isValid = validateField(
      fieldName,
      modelPortfolioData[fieldName as keyof ModelPortfolioData],
    );
    setValidationErrors((prev: Set<string>) => {
      const newErrors = new Set(prev);
      if (!isValid) {
        newErrors.add(fieldName);
      } else {
        newErrors.delete(fieldName);
      }
      return newErrors;
    });
  };

  const isFormValid = () => {
    const requiredFields = Object.keys(options).filter((key) => {
      if (!options[key]?.enabled) return false;
      if (options[key]?.required === FieldRequiredType.NOT_REQUIRED)
        return false;
      return true;
    });

    const fieldsValid = requiredFields.every((field) =>
      validateField(
        field,
        modelPortfolioData[field as keyof ModelPortfolioData],
      ),
    );

    if (
      options?.zeroPercentCash?.enabled &&
      modelPortfolioData.zeroPercentCash
    ) {
      return fieldsValid && !!cashEquivalentSecurity;
    }

    return fieldsValid;
  };

  const submit = async () => {
    if (!isFormValid() || driftToleranceInvalid) return;
    if (isAssetClassAllocationInvalid) return;

    sendAnalytic(
      ovAnalyticsEvents.workflowsAddModelPortfolioDetailsContinueButtonSelect,
      {
        workflowStepTitle: options?.title,
        workflowId: workflowCompletion?.workflow?.id,
        workflowName: workflowCompletion?.workflow?.name,
        activeWorkflowCompletionId: workflowCompletion?.id,
        objectId: workflowCompletion?.objectId,
        objectType: workflowCompletion?.objectType,
      },
    );

    // Track step 3 save analytics
    sendAnalytic(ovAnalyticsEvents.modelPageCreateStep3AddModelDetailsSave, {
      modelName: translateBackendString(modelPortfolioData.translatedName),
      timestamp: new Date().toISOString(),
    });

    continueFunc(modelPortfolioData);
  };

  const updateModelPortfolioData = (newValue: any) => {
    const updatedData = {
      ...modelPortfolioData,
      ...newValue,
    };
    setModelPortfolioData(updatedData);
  };

  const handleCashEquivalentSecurityChange = (security: FinancialProduct) => {
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    const securityToAdd: Security = {
      financialProduct: security,
      isCashEquivalent: true,
      percentage: 0,
      taxRank: 1,
      id: tempId,
      color: 'supportOne',
    };

    const updatedSecurities = [
      ...(modelPortfolioData.securities || []).filter(
        (updateSecurity: Security) => !updateSecurity.isCashEquivalent,
      ),
      securityToAdd,
    ];

    updateModelPortfolioData({
      securities: updatedSecurities,
    });
    setCashEquivalentSecurity(securityToAdd);
  };

  const handleSaveForLater = () => {
    if (
      !isFormValid() ||
      isAssetClassAllocationInvalid ||
      driftToleranceInvalid
    )
      return;
    onSaveForLater(modelPortfolioData);
  };

  const hasChanges = useMemo(
    () => hasModelPortfolioChanges(modelPortfolioData, originalValues),
    [modelPortfolioData, originalValues],
  );

  const isAssetClassAllocationInvalid = useMemo(() => {
    if (modelPortfolioData.strategy !== FinancialProductStrategy.AssetClass)
      return false;
    if (!modelPortfolioData.assetClassLevel) return true;
    return isBranchAllocationInvalid(
      modelPortfolioData.assetClassBranches ?? [],
    );
  }, [
    modelPortfolioData.strategy,
    modelPortfolioData.assetClassLevel,
    modelPortfolioData.assetClassBranches,
  ]);

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('shared:continue'),
        disabled:
          loading ||
          !isFormValid() ||
          isAssetClassAllocationInvalid ||
          driftToleranceInvalid,
        dataTestId: 'continue-button',
      }}
      buttonFooter={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            flex: 1,
          }}
        >
          <Button
            sx={{ mr: 2, textAlign: 'center' }}
            data-testid='save-for-later'
            variant='contained'
            onClick={handleSaveForLater}
            disabled={
              loading ||
              !isFormValid() ||
              isAssetClassAllocationInvalid ||
              driftToleranceInvalid ||
              !hasChanges
            }
            loading={loading}
            color='secondary'
          >
            {translateBackendString(options?.buttonSaveForLater?.label)}
          </Button>
        </Box>
      }
    >
      {options?.title && (
        <Typography
          variant='displayLarge'
          sx={{
            mt: 1,
            mb: 2,
            fontWeight: 400,
            fontSize: '40px',
            fontFamily: 'Tiempos Text',
          }}
        >
          {translateBackendString(options?.title)}
          {additionalInfo(
            translateBackendString,
            options?.title?.additionalInfo,
          ) && (
            <InfoDialog
              information={
                additionalInfo(
                  translateBackendString,
                  options?.title?.additionalInfo,
                ) ?? ''
              }
            />
          )}
        </Typography>
      )}

      {options?.subtitle && (
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} linkTarget={'_blank'}>
            {translateBackendString(options?.subtitle)}
          </ReactMarkdown>
          {additionalInfo(
            translateBackendString,
            options?.subtitle?.additionalInfo,
          ) && (
            <InfoDialog
              information={
                additionalInfo(
                  translateBackendString,
                  options?.subtitle?.additionalInfo,
                ) ?? ''
              }
            />
          )}
        </Typography>
      )}

      {modelPortfolioData.strategy === FinancialProductStrategy.AssetClass &&
        isAssetClassAllocationInvalid && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {t('modelPortfolios:portfolio.incompleteAllocationError')}
          </Alert>
        )}

      <Grid container spacing={2}>
        {options?.strategy?.enabled && (
          <Grid size={{ xs: 12, md: 6 }}>
            <SelectField
              testId='strategy'
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const newStrategy = e.target.value as FinancialProductStrategy;
                const switchingFromAssetClass =
                  modelPortfolioData.strategy ===
                    FinancialProductStrategy.AssetClass &&
                  newStrategy !== FinancialProductStrategy.AssetClass;
                const switchingToAssetClass =
                  newStrategy === FinancialProductStrategy.AssetClass &&
                  modelPortfolioData.strategy !==
                    FinancialProductStrategy.AssetClass;
                if (switchingFromAssetClass) {
                  enqueueToast(
                    t(
                      'modelPortfolios:portfolio.strategyChangedSecuritiesCleared',
                    ),
                    { variant: 'warning' },
                  );
                }
                updateModelPortfolioData({
                  strategy: newStrategy,
                  ...(newStrategy === FinancialProductStrategy.AssetClass
                    ? {}
                    : { assetClassBranches: [], assetClassLevel: undefined }),
                  ...(switchingToAssetClass
                    ? { assetClassLevel: AssetClassLevel.Primary }
                    : {}),
                  ...(switchingToAssetClass &&
                  modelPortfolioData.portfolioTemplate?.assetClassBranches
                    ?.length
                    ? {
                        assetClassBranches:
                          modelPortfolioData.portfolioTemplate
                            .assetClassBranches,
                      }
                    : {}),
                  ...(switchingFromAssetClass ? { children: [] } : {}),
                });
              }}
              disabled={loading}
              label={translateBackendString(options?.strategy?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.strategy?.additionalInfo,
              )}
              fullWidth
              value={
                modelPortfolioData.strategy || FinancialProductStrategy.Standard
              }
              onBlur={() => handleFieldBlur('strategy')}
              error={validationErrors.has('strategy')}
              required={options.strategy.required === FieldRequiredType.ERROR}
            >
              {MODEL_PORTFOLIO_STRATEGIES.filter(
                (strategy) =>
                  strategy !== FinancialProductStrategy.AssetClass ||
                  isAssetClassPortfoliosEnabled,
              ).map((strategy) => (
                <MenuItem
                  key={strategy}
                  value={strategy}
                  data-testid={`type-${kebabCase(strategy)}`}
                >
                  {t(`modelPortfolios:strategy.${strategy}`)}
                </MenuItem>
              ))}
            </SelectField>
          </Grid>
        )}

        {options?.translatedName?.enabled && (
          <Grid size={{ xs: 12, md: 6 }}>
            <TranslatableTextField
              testId='model-name'
              onChange={(value) =>
                updateModelPortfolioData({ translatedName: value })
              }
              disabled={loading}
              label={translateBackendString(options?.translatedName?.label)}
              fullWidth
              value={modelPortfolioData.translatedName || getLanguageDefaults()}
              onBlur={() => handleFieldBlur('translatedName')}
              error={validationErrors.has('translatedName')}
              required={
                options.translatedName.required === FieldRequiredType.ERROR
              }
            />
          </Grid>
        )}

        {options?.translatedPortfolioDescription?.enabled && (
          <Grid size={12}>
            <TranslatableTextField
              testId='model-description'
              onChange={(value) =>
                updateModelPortfolioData({
                  translatedPortfolioDescription: value,
                })
              }
              disabled={loading}
              label={translateBackendString(
                options?.translatedPortfolioDescription?.label,
              )}
              fullWidth
              multiline
              rows={1}
              value={
                modelPortfolioData.translatedPortfolioDescription ||
                getLanguageDefaults()
              }
              onBlur={() => handleFieldBlur('translatedPortfolioDescription')}
              error={validationErrors.has('translatedPortfolioDescription')}
              required={
                options.translatedPortfolioDescription.required ===
                FieldRequiredType.ERROR
              }
            />
          </Grid>
        )}

        {options?.translatedInvestmentObjective?.enabled && (
          <Grid size={12}>
            <TranslatableTextField
              testId='model-investment-objective'
              onChange={(value) =>
                updateModelPortfolioData({
                  translatedInvestmentObjective: value,
                })
              }
              disabled={loading}
              label={translateBackendString(
                options?.translatedInvestmentObjective?.label,
              )}
              fullWidth
              multiline
              rows={1}
              value={
                modelPortfolioData.translatedInvestmentObjective ||
                getLanguageDefaults()
              }
              onBlur={() => handleFieldBlur('translatedInvestmentObjective')}
              error={validationErrors.has('translatedInvestmentObjective')}
              required={
                options.translatedInvestmentObjective.required ===
                FieldRequiredType.ERROR
              }
            />
          </Grid>
        )}

        {options?.riskProfileName?.enabled && (
          <Grid size={12}>
            <TranslatableTextField
              testId='risk-profile'
              onChange={(value) =>
                updateModelPortfolioData({ riskProfileName: value })
              }
              disabled={loading}
              label={translateBackendString(options?.riskProfileName?.label)}
              fullWidth
              value={
                modelPortfolioData.riskProfileName || getLanguageDefaults()
              }
              onBlur={() => handleFieldBlur('riskProfileName')}
              error={validationErrors.has('riskProfileName')}
              required={
                options.riskProfileName.required === FieldRequiredType.ERROR
              }
            />
          </Grid>
        )}

        {options?.themeId?.enabled && (
          <Grid size={{ xs: 12, md: 6 }}>
            <SelectField
              testId='model-family'
              onChange={(e: any) =>
                updateModelPortfolioData({ themeId: e.target.value })
              }
              disabled={loading}
              label={translateBackendString(options?.themeId?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.themeId?.additionalInfo,
              )}
              fullWidth
              value={modelPortfolioData.themeId}
              onBlur={() => handleFieldBlur('themeId')}
              error={validationErrors.has('themeId')}
              required={options.themeId.required === FieldRequiredType.ERROR}
            >
              {themes.map((theme: Theme) => (
                <MenuItem
                  key={theme.id}
                  value={theme.id}
                  data-testid={`theme-${kebabCase(theme.translatedName?.[language] || '')}`}
                >
                  {theme.translatedName?.[language]}
                </MenuItem>
              ))}
            </SelectField>
          </Grid>
        )}

        {options?.riskReturnLevel?.enabled && (
          <Grid size={{ xs: 12, md: 6 }}>
            <NumberField
              testId='risk-return-level'
              setNumber={(number) =>
                updateModelPortfolioData({
                  riskReturnLevel: parseFloat(number),
                })
              }
              disabled={loading}
              label={translateBackendString(options?.riskReturnLevel?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.riskReturnLevel?.additionalInfo,
              )}
              number={modelPortfolioData.riskReturnLevel?.toString() ?? ''}
              onBlur={() => handleFieldBlur('riskReturnLevel')}
              error={validationErrors.has('riskReturnLevel')}
              required={
                options.riskReturnLevel.required === FieldRequiredType.ERROR
              }
              unsigned
            />
          </Grid>
        )}

        {modelPortfolioData.strategy ===
          FinancialProductStrategy.AssetClass && (
          <Grid size={{ xs: 12, md: 6 }}>
            <SelectField
              testId='asset-class-level-select'
              label={t('modelPortfolios:assetClassLevel')}
              value={modelPortfolioData.assetClassLevel ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateModelPortfolioData({
                  assetClassLevel: e.target.value as AssetClassLevel,
                })
              }
              fullWidth
              required
            >
              {enumValues(AssetClassLevel)
                .filter(
                  (level) =>
                    level !== AssetClassLevel.Tertiary ||
                    modelPortfolioData.assetClassLevel ===
                      AssetClassLevel.Tertiary,
                )
                .map((level) => (
                  <MenuItem
                    key={level}
                    value={level}
                    data-testid={`level-${kebabCase(level)}`}
                  >
                    {t(`modelPortfolios:${level}`)}
                  </MenuItem>
                ))}
            </SelectField>
          </Grid>
        )}

        {modelPortfolioData.strategy ===
          FinancialProductStrategy.AssetClass && (
          <Grid size={12}>
            <AssetClassBranchesSelect
              testId='asset-class-branches-editor'
              assetClassBranches={modelPortfolioData.assetClassBranches ?? []}
              setAssetClassBranches={(branches) =>
                updateModelPortfolioData({ assetClassBranches: branches })
              }
            />
          </Grid>
        )}

        {options?.driftSettings?.enabled && (
          <DriftSettingsSection
            organizationId={modelPortfolioData.organizationId}
            method={modelPortfolioData.driftCalculationMethod}
            tolerancePercent={modelPortfolioData.driftTolerancePercent}
            toleranceInvalid={driftToleranceInvalid}
            loading={loading}
            sectionLabel={options?.driftSettings?.label}
            onChange={updateModelPortfolioData}
          />
        )}

        <Grid container spacing={2} sx={{ mt: 1, mb: 1 }} size={12}>
          {options?.suggestable?.enabled && (
            <Grid size={{ xs: 12, md: 4 }}>
              <SwitchField
                data-testid='suggestable'
                onChange={(e: any) =>
                  updateModelPortfolioData({ suggestable: e })
                }
                disabled={loading}
                label={translateBackendString(options?.suggestable?.label)}
                checked={modelPortfolioData.suggestable}
              />
            </Grid>
          )}

          {options?.zeroPercentCash?.enabled && (
            <Grid size={{ xs: 12, md: 3 }}>
              <SwitchField
                data-testid='zero-percent-cash'
                onChange={(e: any) => {
                  const newValue = e || options?.zeroPercentCash?.defaultValue;
                  if (!newValue) {
                    const updatedSecurities = (
                      modelPortfolioData.securities || []
                    ).filter(
                      (security: Security) => !security.isCashEquivalent,
                    );
                    updateModelPortfolioData({
                      zeroPercentCash: newValue,
                      securities: updatedSecurities,
                    });

                    setCashEquivalentSecurity(null);
                  } else {
                    updateModelPortfolioData({ zeroPercentCash: newValue });
                  }
                }}
                disabled={loading}
                label={translateBackendString(options?.zeroPercentCash?.label)}
                checked={
                  modelPortfolioData.zeroPercentCash ||
                  options?.zeroPercentCash?.defaultValue
                }
              />
            </Grid>
          )}
        </Grid>

        {options?.zeroPercentCash?.enabled &&
          modelPortfolioData.zeroPercentCash && (
            <Grid size={12}>
              <SecuritySelect
                testId='cash-equivalent-security'
                setSecurity={(security) => {
                  if (!security || security === 'ANY') return;
                  handleCashEquivalentSecurityChange(security);
                }}
                value={cashEquivalentSecurity?.financialProduct.id || ''}
                label={t('modelPortfolios:cashEquivalentSecurity')}
                disabledWith={loading ? t('shared:loading') : null}
                required
              />
            </Grid>
          )}

        {options?.lowThresholdScore?.enabled && (
          <Grid size={{ xs: 12, md: 4 }}>
            <NumberField
              testId='low-threshold-score'
              setNumber={(number) =>
                updateModelPortfolioData({
                  lowThresholdScore: parseFloat(number),
                })
              }
              disabled={loading}
              label={translateBackendString(options?.lowThresholdScore?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.lowThresholdScore?.additionalInfo,
              )}
              number={modelPortfolioData.lowThresholdScore?.toString() ?? ''}
              onBlur={() => handleFieldBlur('lowThresholdScore')}
              error={validationErrors.has('lowThresholdScore')}
              required={
                options.lowThresholdScore.required === FieldRequiredType.ERROR
              }
              unsigned
            />
          </Grid>
        )}

        {options?.suggestedMinScore?.enabled && (
          <Grid size={{ xs: 12, md: 4 }}>
            <NumberField
              testId='suggested-min-score'
              setNumber={(number) =>
                updateModelPortfolioData({
                  suggestedMinScore: parseFloat(number),
                })
              }
              disabled={loading}
              label={translateBackendString(options?.suggestedMinScore?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.suggestedMinScore?.additionalInfo,
              )}
              number={modelPortfolioData.suggestedMinScore?.toString() ?? ''}
              onBlur={() => handleFieldBlur('suggestedMinScore')}
              error={validationErrors.has('suggestedMinScore')}
              required={
                options.suggestedMinScore.required === FieldRequiredType.ERROR
              }
              unsigned
            />
          </Grid>
        )}

        {options?.suggestedMaxScore?.enabled && (
          <Grid size={{ xs: 12, md: 4 }}>
            <NumberField
              testId='suggested-max-score'
              setNumber={(number) =>
                updateModelPortfolioData({
                  suggestedMaxScore: parseFloat(number),
                })
              }
              disabled={loading}
              label={translateBackendString(options?.suggestedMaxScore?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.suggestedMaxScore?.additionalInfo,
              )}
              number={modelPortfolioData.suggestedMaxScore?.toString() ?? ''}
              onBlur={() => handleFieldBlur('suggestedMaxScore')}
              error={validationErrors.has('suggestedMaxScore')}
              required={
                options.suggestedMaxScore.required === FieldRequiredType.ERROR
              }
              unsigned
            />
          </Grid>
        )}

        {options?.forecastedRateOfReturn?.enabled && (
          <Grid size={{ xs: 12, md: 6 }}>
            <NumberField
              testId='forecasted-rate-of-return'
              setNumber={(number) =>
                updateModelPortfolioData({
                  forecastedRateOfReturn: parseFloat(number),
                })
              }
              trailingIcon='percent'
              disabled={loading}
              label={translateBackendString(
                options?.forecastedRateOfReturn?.label,
              )}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.forecastedRateOfReturn?.additionalInfo,
              )}
              number={
                modelPortfolioData.forecastedRateOfReturn?.toString() ?? ''
              }
              onBlur={() => handleFieldBlur('forecastedRateOfReturn')}
              error={validationErrors.has('forecastedRateOfReturn')}
              required={
                options.forecastedRateOfReturn.required ===
                FieldRequiredType.ERROR
              }
              unsigned
            />
          </Grid>
        )}

        {options?.forecastedStandardDeviation?.enabled && (
          <Grid size={{ xs: 12, md: 6 }}>
            <NumberField
              testId='forecasted-standard-deviation'
              setNumber={(number) =>
                updateModelPortfolioData({
                  forecastedStandardDeviation: parseFloat(number),
                })
              }
              trailingIcon='percent'
              disabled={loading}
              label={translateBackendString(
                options?.forecastedStandardDeviation?.label,
              )}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.forecastedStandardDeviation?.additionalInfo,
              )}
              number={
                modelPortfolioData.forecastedStandardDeviation?.toString() ?? ''
              }
              onBlur={() => handleFieldBlur('forecastedStandardDeviation')}
              error={validationErrors.has('forecastedStandardDeviation')}
              required={
                options.forecastedStandardDeviation.required ===
                FieldRequiredType.ERROR
              }
              unsigned
            />
          </Grid>
        )}

        {options?.url?.enabled && (
          <Grid size={12}>
            <TranslatableTextField
              testId='url'
              onChange={(value) =>
                updateModelPortfolioData({ translatedUrl: value })
              }
              disabled={loading}
              label={translateBackendString(options?.url?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.url?.additionalInfo,
              )}
              fullWidth
              value={modelPortfolioData.translatedUrl || getLanguageDefaults()}
              onBlur={() => handleFieldBlur('translatedUrl')}
              error={validationErrors.has('translatedUrl')}
              required={options.url.required === FieldRequiredType.ERROR}
            />
          </Grid>
        )}
      </Grid>
    </WorkflowCompletionForm>
  );
};

export default AddModelPortfolioDetailsVisual;
